import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { BrowserTracing } from '@sentry/tracing';
import { AppModule } from './app/app.module';

const localDevMode = () => {
  return window.location.hostname === 'localhost';
};
const hostname = window.location.hostname;
const isDevelopment = process?.env?.NODE_ENV === 'development';

if (!localDevMode()) {
  enableProdMode();
  Sentry.init({
    dsn: 'https://70e95323ee1c472e97813a6baae3c45e@o1224650.ingest.sentry.io/6369684',
    integrations: [
      Sentry.replayIntegration(),
      new BrowserTracing({
        tracingOrigins: ['/.*console.facets.cloud.*/'],
        routingInstrumentation: Sentry.routingInstrumentation,
      }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: isDevelopment ? 0.3 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        hostname: hostname,
      };

      if (event.exception && event.exception.values) {
        const message = event.exception.values[0].value || '';
        const type = event.exception.values[0].type || '';

        // Discard and fingerprint specific runtime errors
        if (
          message.includes('Loading chunk') ||
          message.includes('zone.runTask') ||
          message.includes('SyntheticException')
        ) {
          event.fingerprint = ['runtime-exceptions', type];
          return null; // Discard the event
        }

        // High Priority: Authentication Errors
        if (
          message.includes('Invalid Credentials') ||
          message.includes('Token Expired') ||
          message.includes('Token Not Provided') ||
          message.includes('Unauthorized Access Attempt') ||
          message.includes('Permission Denied')
        ) {
          event.fingerprint = ['auth-error', type];
          event.level = 'error';
        }

        // High Priority: Server-Side Errors
        else if (
          message.includes('Internal Server Error') ||
          message.includes('Bad Gateway') ||
          message.includes('Service Unavailable') ||
          message.includes('Gateway Timeout') ||
          message.includes('Database Connection Error')
        ) {
          event.fingerprint = ['server-error', type];
          event.level = 'error';
        }

        // Medium Priority: Client-Side Errors
        else if (
          type === 'TypeError' ||
          type === 'ReferenceError' ||
          type === 'SyntaxError' ||
          type === 'RangeError' ||
          type === 'EvalError' ||
          type === 'URIError'
        ) {
          event.fingerprint = ['client-error', type];
          event.level = 'warning';
        }

        // Medium Priority: Network Errors
        else if (
          message.includes('Request Timeout') ||
          message.includes('DNS Resolution Failure') ||
          message.includes('Network Connection Lost') ||
          message.includes('CORS Errors')
        ) {
          event.fingerprint = ['network-error', type];
          event.level = 'warning';
        }

        // Medium Priority: API Errors
        else if (
          message.includes('400 Bad Request') ||
          message.includes('401 Unauthorized') ||
          message.includes('403 Forbidden') ||
          message.includes('404 Not Found') ||
          message.includes('409 Conflict') ||
          message.includes('422 Unprocessable Entity')
        ) {
          event.fingerprint = ['api-error', type];
          event.level = 'warning';
        }

        // Low Priority: Resource Loading Errors
        else if (
          message.includes('Image Load Failure') ||
          message.includes('Script Load Failure') ||
          message.includes('CSS Load Failure') ||
          message.includes('Font Loading Issues')
        ) {
          event.fingerprint = ['resource-loading-error', type];
          event.level = 'info';
        }

        // Low Priority: Performance and UI Errors
        else if (
          message.includes('Long Task Detected') ||
          message.includes('Unresponsive UI') ||
          message.includes('Memory Leaks') ||
          message.includes('Animation/Rendering Errors')
        ) {
          event.fingerprint = ['performance-ui-error', type];
          event.level = 'info';
        }

        // Custom Errors: Project-Specific
        else if (
          message.includes('Custom Application Errors') ||
          message.includes('Workflow Failures') ||
          message.includes('Feature Flag Errors')
        ) {
          event.fingerprint = ['custom-error', type];
          event.level = 'info';
        }

        // Default fingerprint for uncategorized errors
        else {
          event.fingerprint = ['{{ default }}', type];
        }
      }

      return event;
    },
  });
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
